import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
    getTargetTimezoneDate as utilGetTargetTimezoneDate,
    hasAvailableWorkingHours as utilHasAvailableWorkingHours,
    getUtcMidnightTimestampForDateInTimezone,
} from '../utils/timezone-helpers';
import {
    MONTH_NAMES,
    DAY_NAMES_WEEK_START_MONDAY,
    MAX_LOOKAHEAD_DAYS,
} from '../constants';

interface FreeDay {
    date: string;
    freeSlots: Array<{ label: string; timestamp: number }>;
}

const normalizeToMidnight = (date: Date): Date => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
};

const formatDateKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const EMPTY_DISABLED_WEEKDAYS: number[] = [];

interface CalendarProps {
    /** Timezone identifier (e.g., 'Pacific/Auckland', 'Australia/Sydney') */
    timeZone?: string;
    /** Callback function called when a date is selected, receives timestamp */
    onDateSelect?: (timestamp: number) => void;
    /** Initial selected date */
    initialSelectedDate?: Date;
    /** Selected date in UTC milliseconds, keeps the calendar in sync */
    selectedTimestamp?: number;
    /** Free days with available slots */
    freeDays?: FreeDay[];
    /** Weekday indexes (0 = Sunday ... 6 = Saturday) that should be disabled */
    disabledWeekdays?: number[];
    /** Month select layout */
    naviVariant?: 'center' | 'right';
}

/**
 * Rocco Calendar component
 */
const Calendar: React.FC<CalendarProps> = ({
    timeZone = 'Pacific/Auckland',
    onDateSelect,
    initialSelectedDate,
    selectedTimestamp,
    freeDays,
    disabledWeekdays: disabledWeekdaysProp,
    naviVariant = 'center',
}) => {
    const disabledWeekdays = disabledWeekdaysProp ?? EMPTY_DISABLED_WEEKDAYS;
    const freeDateStrings = useMemo(() => {
        if (!freeDays || freeDays.length === 0) {
            return new Set<string>();
        }
        // Mark days as free if they have available slots
        return new Set(
            freeDays
                .filter((day) => day.freeSlots && day.freeSlots.length > 0)
                .map((day) => day.date),
        );
    }, [freeDays]);

    // Convert timestamp to target timezone date using Intl
    const getTargetTimezoneDate = useCallback(
        (timestamp?: number): Date =>
            utilGetTargetTimezoneDate(timeZone, timestamp),
        [timeZone],
    );

    // Check if a date has available working hours (9am-4pm in target timezone)
    const hasAvailableWorkingHours = useCallback(
        (date: Date): boolean => utilHasAvailableWorkingHours(timeZone, date),
        [timeZone],
    );

    const isDateFree = useCallback(
        (date: Date) => freeDateStrings.has(formatDateKey(date)),
        [freeDateStrings],
    );

    const isDateDisabledByWeekday = useCallback(
        (date: Date) => disabledWeekdays.includes(date.getDay()),
        [disabledWeekdays],
    );

    const isDateSelectable = useCallback(
        (date: Date) => {
            const normalized = normalizeToMidnight(date);
            return (
                hasAvailableWorkingHours(normalized) &&
                !isDateDisabledByWeekday(normalized) &&
                isDateFree(normalized)
            );
        },
        [hasAvailableWorkingHours, isDateDisabledByWeekday, isDateFree],
    );

    const findNextSelectableDate = useCallback(
        (startDate: Date) => {
            const candidate = normalizeToMidnight(startDate);
            for (let i = 0; i < MAX_LOOKAHEAD_DAYS; i++) {
                if (isDateSelectable(candidate)) {
                    return new Date(candidate);
                }
                candidate.setDate(candidate.getDate() + 1);
            }
            return null;
        },
        [isDateSelectable],
    );

    const emitSelection = useCallback(
        (date: Date) => {
            if (!onDateSelect) {
                return;
            }

            const localMidnight = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                0,
                0,
                0,
                0,
            );

            const midnightInTargetTimezone =
                getUtcMidnightTimestampForDateInTimezone(
                    localMidnight,
                    timeZone,
                );
            onDateSelect(midnightInTargetTimezone);
        },
        [onDateSelect, timeZone],
    );

    const selectedDateFromTimestamp = useMemo(() => {
        if (selectedTimestamp === undefined || selectedTimestamp === null) {
            return null;
        }
        const dateInZone = getTargetTimezoneDate(selectedTimestamp);
        dateInZone.setHours(0, 0, 0, 0);
        return dateInZone;
    }, [selectedTimestamp, getTargetTimezoneDate]);

    const hasProcessedInitialSelectedTimestamp = useRef(false);

    const [currentDate, setCurrentDate] = useState(() =>
        getTargetTimezoneDate(),
    );
    const [selectedDate, setSelectedDate] = useState<Date | null>(
        initialSelectedDate || null,
    );

    const todayInTarget = getTargetTimezoneDate();

    // Initialize selected date to today if available, else next working day
    useEffect(() => {
        if (selectedDate) {
            return;
        }

        const nextDate = findNextSelectableDate(getTargetTimezoneDate());
        if (!nextDate) {
            setSelectedDate(null);
            setCurrentDate(getTargetTimezoneDate());
            return;
        }

        setCurrentDate(new Date(nextDate));
        setSelectedDate(new Date(nextDate));
        emitSelection(nextDate);
    }, [
        selectedDate,
        findNextSelectableDate,
        getTargetTimezoneDate,
        emitSelection,
    ]);

    useEffect(() => {
        if (!selectedDateFromTimestamp) {
            return;
        }

        if (!hasProcessedInitialSelectedTimestamp.current) {
            hasProcessedInitialSelectedTimestamp.current = true;
            return;
        }

        const nextDate = findNextSelectableDate(selectedDateFromTimestamp);
        if (!nextDate) {
            setSelectedDate(null);
            return;
        }

        let didChange = false;
        setSelectedDate((prev) => {
            if (prev?.getTime() === nextDate.getTime()) {
                return prev;
            }
            didChange = true;
            return new Date(nextDate);
        });

        setCurrentDate((prev) => {
            if (
                prev.getFullYear() === nextDate.getFullYear() &&
                prev.getMonth() === nextDate.getMonth()
            ) {
                return prev;
            }

            return new Date(nextDate);
        });

        if (
            didChange ||
            nextDate.getTime() !== selectedDateFromTimestamp.getTime()
        ) {
            emitSelection(nextDate);
        }
    }, [selectedDateFromTimestamp, findNextSelectableDate, emitSelection]);

    useEffect(() => {
        if (!selectedDate) {
            return;
        }

        if (isDateSelectable(selectedDate)) {
            return;
        }

        const nextDate = findNextSelectableDate(selectedDate);
        if (!nextDate) {
            setSelectedDate(null);
            return;
        }

        setSelectedDate(new Date(nextDate));
        setCurrentDate((prev) => {
            if (
                prev.getFullYear() === nextDate.getFullYear() &&
                prev.getMonth() === nextDate.getMonth()
            ) {
                return prev;
            }

            return new Date(nextDate);
        });
        emitSelection(nextDate);
    }, [selectedDate, isDateSelectable, findNextSelectableDate, emitSelection]);

    const getDaysInMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
        const firstDay = new Date(
            date.getFullYear(),
            date.getMonth(),
            1,
        ).getDay();
        // Convert Sunday (0) to 7, and shift all days by -1 to make Monday = 0
        return firstDay === 0 ? 6 : firstDay - 1;
    };

    // Check if month navigation should be disabled
    // Only allow current month and next month (1 month ahead)
    const canNavigateToPreviousMonth = (): boolean => {
        const previousMonth = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() - 1,
            1,
        );
        const currentMonth = new Date(
            todayInTarget.getFullYear(),
            todayInTarget.getMonth(),
            1,
        );
        return previousMonth >= currentMonth;
    };

    const canNavigateToNextMonth = (): boolean => {
        const nextMonth = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + 1,
            1,
        );
        const oneMonthFromNow = new Date(
            todayInTarget.getFullYear(),
            todayInTarget.getMonth() + 1,
            1,
        );
        return nextMonth <= oneMonthFromNow;
    };

    const navigateMonth = (direction: number) => {
        if (direction === -1 && !canNavigateToPreviousMonth()) return;
        if (direction === 1 && !canNavigateToNextMonth()) return;

        setCurrentDate(
            new Date(
                currentDate.getFullYear(),
                currentDate.getMonth() + direction,
                1,
            ),
        );
    };

    // Handle date selection
    const handleDateSelect = (day: number) => {
        const selectedDateTime = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            day,
        );

        if (!isDateSelectable(selectedDateTime)) return;

        setSelectedDate(selectedDateTime);
        emitSelection(selectedDateTime);
    };

    const CalendarDays = () => {
        const daysInMonth = getDaysInMonth(currentDate);
        const firstDayOfMonth = getFirstDayOfMonth(currentDate);

        const days: React.ReactElement[] = [];
        const todayNormalized = getTargetTimezoneDate();
        todayNormalized.setHours(0, 0, 0, 0);

        // Previous month's trailing days (hidden)
        for (let i = firstDayOfMonth - 1; i >= 0; i--) {
            days.push(
                <div key={`prev-${i}`} className="py-4 text-center">
                    {/* Empty space for alignment */}
                </div>,
            );
        }

        // Current month's days
        for (let day = 1; day <= daysInMonth; day++) {
            const dayDate = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth(),
                day,
            );
            dayDate.setHours(0, 0, 0, 0);

            const isToday = dayDate.getTime() === todayNormalized.getTime();
            const isSelected =
                selectedDate &&
                day === selectedDate.getDate() &&
                currentDate.getMonth() === selectedDate.getMonth() &&
                currentDate.getFullYear() === selectedDate.getFullYear();

            const isDisabled = !isDateSelectable(dayDate);

            let dayClasses = 'relative py-4 text-center transition-colors';
            let backgroundElement: React.ReactElement | null = null;

            if (isDisabled) {
                dayClasses += ' text-gray-300 cursor-not-allowed';
                days.push(
                    <div key={`current-${day}-disabled`} className={dayClasses}>
                        {backgroundElement}
                        <span className="relative z-10">{day}</span>
                    </div>,
                );
            } else {
                dayClasses += ' cursor-pointer';
                backgroundElement = (
                    <div className="group absolute inset-0 flex items-center justify-center">
                        <div className="h-8 w-8 rounded-full transition-colors duration-100 group-hover:bg-gray-100"></div>
                    </div>
                );

                if (isSelected) {
                    dayClasses += ' text-white';
                    backgroundElement = (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="h-8 w-8 rounded-full bg-brick-500"></div>
                        </div>
                    );
                } else if (isToday) {
                    // If another date is selected (not today), show today with gray styling
                    const hasOtherDateSelected = selectedDate && !isSelected;
                    if (hasOtherDateSelected) {
                        dayClasses += ' text-gray-700';
                        backgroundElement = (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="h-8 w-8 rounded-full bg-gray-300"></div>
                            </div>
                        );
                    } else {
                        // No date selected or today is selected, show with brick color
                        dayClasses += ' text-white';
                        backgroundElement = (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="h-8 w-8 rounded-full bg-brick-500"></div>
                            </div>
                        );
                    }
                } else {
                    dayClasses += ' text-gray-900';
                }
                days.push(
                    <div
                        key={`current-${day}`}
                        className={dayClasses}
                        onClick={() => !isDisabled && handleDateSelect(day)}
                    >
                        {backgroundElement}
                        <span className="relative z-10">{day}</span>
                    </div>,
                );
            }
        }

        // Only add empty cells if needed to complete the current week
        const totalCells = days.length;
        const cellsInLastWeek = totalCells % 7;
        if (cellsInLastWeek !== 0) {
            const remainingCellsInWeek = 7 - cellsInLastWeek;
            for (let day = 1; day <= remainingCellsInWeek; day++) {
                days.push(
                    <div key={`next-${day}`} className="py-2 text-center">
                        {/* Empty space for alignment */}
                    </div>,
                );
            }
        }

        return <>{days}</>;
    };

    return (
        <div className="mx-auto w-full">
            {/* Header */}

            {naviVariant === 'center' ? (
                <div className="mb-3 flex items-center justify-center">
                    <button
                        onClick={() => navigateMonth(-1)}
                        disabled={!canNavigateToPreviousMonth()}
                        className={`rounded-full p-1 transition-colors ${
                            canNavigateToPreviousMonth()
                                ? 'text-gray-600 hover:bg-gray-100'
                                : 'invisible'
                        }`}
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </button>

                    <h2 className="text-5.5 font-semibold text-gray-900">
                        {MONTH_NAMES[currentDate.getMonth()]}{' '}
                        {currentDate.getFullYear()}
                    </h2>

                    <button
                        onClick={() => navigateMonth(1)}
                        disabled={!canNavigateToNextMonth()}
                        className={`rounded-full p-1 transition-colors ${
                            canNavigateToNextMonth()
                                ? 'text-gray-600 hover:bg-gray-100'
                                : 'invisible'
                        }`}
                    >
                        <ChevronRight className="h-5 w-5" />
                    </button>
                </div>
            ) : (
                <div className="mb-6 flex w-full items-center justify-between px-2">
                    <h2 className="text-4 font-medium text-gray-900">
                        {MONTH_NAMES[currentDate.getMonth()]}{' '}
                        {currentDate.getFullYear()}
                    </h2>

                    <nav className="flex items-center gap-2">
                        <button
                            onClick={() => navigateMonth(-1)}
                            disabled={!canNavigateToPreviousMonth()}
                            className={`${
                                canNavigateToPreviousMonth()
                                    ? 'text-gray-600 hover:bg-gray-100'
                                    : 'invisible'
                            }`}
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                            onClick={() => navigateMonth(1)}
                            disabled={!canNavigateToNextMonth()}
                            className={`${
                                canNavigateToNextMonth()
                                    ? 'text-gray-600 hover:bg-gray-100'
                                    : 'invisible'
                            }`}
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>
                    </nav>
                </div>
            )}

            {/* Days of week */}
            <div className="grid grid-cols-7">
                {DAY_NAMES_WEEK_START_MONDAY.map((day) => (
                    <div
                        key={day}
                        className="text-center text-4.5 text-gray-600"
                    >
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-0 font-medium">
                <CalendarDays />
            </div>
        </div>
    );
};

export { Calendar };
